import type {
  BillingStats,
  BillingStatsBreakdownClient,
  BillingStatsLine,
  BillingStatsMonth,
} from '@fos/shared/contracts/billing';
import { billingMonthName } from '@fos/shared/labels';
import { formatPLN, NBSP } from '@fos/shared/money';
import { keepPreviousData } from '@tanstack/react-query';
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  ReceiptText,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { createContext, useContext, useState } from 'react';
import { cn } from '../../../lib/cn';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Stat } from '../../../ui/Stat';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { ClientDot } from '../../clients';
import { clockText, hoursMinutesText, moneyAuto, moneyShort, useBillingMode } from '../billing-format';

/**
 * Rozliczenia → Statystyki (prośba Sebastiana 9.10.2026; czeka na projekt w Figmie — wzorce kafelków i sekcji
 * z Rozliczeń): przełącznik roku, cztery liczby (netto, opłacone, czeka na zapłatę, niezafakturowane), wykres
 * miesięcy z cienkim słupkiem roku poprzedniego, tabela miesięcy i „Z czego składa się miesiąc” (klienci → projekty).
 * Kwoty z serwera (`billing.stats`: moduł Rozliczenia + „Widzi stawki i kwoty”), przychód wg daty wystawienia.
 */

const MONTH_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'] as const;

const monthName = (year: number, month: number) => billingMonthName(`${year}-${String(month).padStart(2, '0')}-01`);
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const isZero = (amount: string) => Number(amount) === 0;
const percentText = (share: number) =>
  `${share.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}${NBSP}%`;
/** Tryb rozliczeń z klientem (bez systemu faktur, DECISIONS 2026-10-09): „rozliczenia” zamiast „faktur”. */
const SettlementContext = createContext(false);

function useInvoicesText(): (n: number) => string {
  const settlement = useContext(SettlementContext);
  return (n) =>
    `${n} ${plural(n, settlement ? ['rozliczenie', 'rozliczenia', 'rozliczeń'] : ['faktura', 'faktury', 'faktur'])}`;
}

export function BillingStatsView() {
  const { settlement } = useBillingMode();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState<number | undefined>(undefined);
  const query = trpc.billing.stats.useQuery({ year, month }, { placeholderData: keepPreviousData });

  const changeYear = (next: number) => {
    setYear(next);
    setMonth(undefined);
  };

  if (query.isPending) {
    return (
      <div role="status" aria-label="Wczytywanie statystyk" className="mt-6 flex flex-col gap-4">
        <Skeleton className="h-8 w-40 rounded-md" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[118px] rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-[260px] rounded-xl" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Section className="mt-6">
        <ErrorState
          title="Nie udało się wczytać statystyk"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </Section>
    );
  }

  const s = query.data;
  const years = s.years.includes(year) ? s.years : [...s.years, year].sort((a, b) => a - b);
  const minYear = years[0] ?? year;
  const maxYear = Math.max(new Date().getFullYear(), ...years);
  const empty = s.totals.invoices === 0;

  return (
    <SettlementContext.Provider value={settlement}>
      <div className={cn('mt-6 flex flex-col gap-4 md:gap-6', query.isFetching && 'opacity-90')}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <fieldset className="m-0 flex items-center gap-1 border-0 p-0" aria-label="Rok">
            <Button
              variant="ghost"
              icon={ChevronLeft}
              aria-label="Poprzedni rok"
              className="max-md:size-11"
              disabled={year <= minYear}
              onClick={() => changeYear(year - 1)}
            />
            <Mono className="min-w-[48px] text-center text-h3 font-semibold text-fg" aria-live="polite">
              {year}
            </Mono>
            <Button
              variant="ghost"
              icon={ChevronRight}
              aria-label="Następny rok"
              className="max-md:size-11"
              disabled={year >= maxYear}
              onClick={() => changeYear(year + 1)}
            />
          </fieldset>
          <p className="text-caption text-fg-muted">
            {settlement ? 'Przychód netto wg daty wysłania rozliczenia' : 'Przychód netto wg daty wystawienia faktury'}
          </p>
        </div>

        <YearTiles stats={s} />
        {s.otherCurrencies.length > 0 && (
          <p className="-mt-2 text-caption text-fg-muted md:-mt-4">
            Poza tym w {year} wystawiono{' '}
            {s.otherCurrencies.map((c) => formatPLN(c.amount, { decimals: false, currency: c.currency })).join(' + ')}{' '}
            (bez przeliczania na złote).
          </p>
        )}

        {empty ? (
          <Section>
            <EmptyState
              icon={BarChart3}
              title={settlement ? `Brak wysłanych rozliczeń w ${year} roku` : `Brak wystawionych faktur w ${year} roku`}
              description={
                settlement
                  ? 'Rozliczenie liczy się od „Oznacz jako wysłane klientowi”.'
                  : 'Odnotuj je w zestawieniach przez „Oznacz jako wystawione”.'
              }
            />
          </Section>
        ) : (
          <>
            <Section aria-labelledby="billing-stats-months">
              <SectionHeader
                title={<span id="billing-stats-months">Miesiące</span>}
                tone="strong"
                action={<ChartLegend year={year} />}
              />
              <div className="flex flex-col gap-4">
                <div className="order-2 md:order-1">
                  <MonthsChart stats={s} selected={s.breakdown.month} onSelect={setMonth} />
                </div>
                <div className="order-1 md:order-2">
                  <MonthsTable stats={s} selected={s.breakdown.month} onSelect={setMonth} />
                </div>
              </div>
            </Section>
            <div className="grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <MonthBreakdown stats={s} />
              <TopClients stats={s} />
            </div>
          </>
        )}
      </div>
    </SettlementContext.Provider>
  );
}

function YearTiles({ stats: s }: { stats: BillingStats }) {
  const t = s.totals;
  const invoicesText = useInvoicesText();
  const settlement = useContext(SettlementContext);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Stat
        label="Przychód netto"
        icon={TrendingUp}
        value={moneyShort(t.net)}
        note={isZero(t.prevNet) ? invoicesText(t.invoices) : `w ${s.year - 1}: ${moneyShort(t.prevNet)}`}
        showDot={false}
      />
      <Stat
        label="Opłacone"
        icon={Wallet}
        value={moneyShort(t.paid)}
        note={isZero(t.paid) ? 'jeszcze nic' : invoicesText(t.invoices - t.unpaidCount)}
        tone="success"
        showDot={!isZero(t.paid)}
      />
      <Stat
        label="Czeka na zapłatę"
        icon={ReceiptText}
        value={moneyShort(t.unpaid)}
        note={t.unpaidCount === 0 ? 'wszystko opłacone' : invoicesText(t.unpaidCount)}
        tone="warning"
        showDot={t.unpaidCount > 0}
        to={t.unpaidCount > 0 ? '/rozliczenia?status=do-zaplaty' : undefined}
      />
      <Stat
        label={settlement ? 'Nierozliczone' : 'Niezafakturowane'}
        icon={Clock}
        value={moneyShort(t.unbilled)}
        note={t.unbilledSeconds === 0 ? 'wszystko rozliczone' : `${hoursMinutesText(t.unbilledSeconds)} czasu`}
        showDot={false}
      />
    </div>
  );
}

function ChartLegend({ year }: { year: number }) {
  return (
    <div className="flex items-center gap-3 text-caption text-fg-muted" aria-hidden>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-xs bg-fg-muted" />
        {year}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-1 rounded-xs bg-line-strong" />
        {year - 1}
      </span>
    </div>
  );
}

/** Słupki miesięcy (CSS): netto roku i cienki, blady słupek roku poprzedniego; wybrany miesiąc w kolorze akcentu. */
function MonthsChart({
  stats: s,
  selected,
  onSelect,
}: {
  stats: BillingStats;
  selected: number;
  onSelect: (month: number) => void;
}) {
  const max = Math.max(0, ...s.months.flatMap((m) => [Number(m.net), Number(m.prevNet)]));
  const height = (amount: string) => {
    const v = Number(amount);
    if (max <= 0 || v <= 0) return '0%';
    return `${Math.max(2, (v / max) * 100)}%`;
  };
  return (
    <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
      <div className="grid h-[132px] min-w-[300px] grid-cols-12 gap-1 md:h-[180px] md:gap-3">
        {s.months.map((m) => {
          const active = m.month === selected;
          return (
            <button
              key={m.month}
              type="button"
              aria-pressed={active}
              aria-label={`${capitalize(monthName(s.year, m.month))}: ${moneyShort(m.net)} netto, w ${s.year - 1}: ${moneyShort(m.prevNet)}`}
              title={`${capitalize(monthName(s.year, m.month))}: ${moneyShort(m.net)} (${s.year - 1}: ${moneyShort(m.prevNet)})`}
              onClick={() => onSelect(m.month)}
              className="group flex h-full min-w-0 flex-col items-stretch gap-1.5 rounded-md focus-visible:outline-2 focus-visible:outline-accent"
            >
              <span className="flex flex-1 items-end justify-center gap-[3px] border-b border-line">
                <span className="w-1 shrink-0 rounded-t-xs bg-line-strong" style={{ height: height(m.prevNet) }} />
                <span
                  className={cn(
                    'w-full max-w-8 rounded-t-xs transition-colors',
                    active ? 'bg-accent' : 'bg-fg-muted group-hover:bg-fg-secondary',
                  )}
                  style={{ height: height(m.net) }}
                />
              </span>
              <span className={cn('text-center text-caption', active ? 'font-medium text-fg' : 'text-fg-muted')}>
                {MONTH_SHORT[m.month - 1]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Tabela miesięcy (netto, opłacone, faktury, godziny); na telefonie lista z kwotą i krótką notatką. */
function MonthsTable({
  stats: s,
  selected,
  onSelect,
}: {
  stats: BillingStats;
  selected: number;
  onSelect: (month: number) => void;
}) {
  const th = 'h-9 px-1 text-caption font-medium text-fg-muted';
  const invoicesText = useInvoicesText();
  const settlement = useContext(SettlementContext);
  const row = (m: BillingStatsMonth) => m.month === selected;
  return (
    <>
      <ul className="md:hidden" aria-label="Miesiące">
        {s.months.map((m) => (
          <li key={m.month} className="border-b border-line">
            <button
              type="button"
              aria-pressed={row(m)}
              onClick={() => onSelect(m.month)}
              className={cn(
                '-mx-2 flex min-h-[52px] w-[calc(100%+16px)] items-center gap-3 rounded-md px-2 py-2 text-left',
                row(m) && 'bg-surface',
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col">
                <span className={cn('text-body-s text-fg', row(m) && 'font-semibold')}>
                  {capitalize(monthName(s.year, m.month))}
                </span>
                {m.invoices > 0 && (
                  <span className="truncate text-caption text-fg-muted">
                    {invoicesText(m.invoices)} · opłacone {moneyShort(m.paid)} · {clockText(m.seconds)}
                  </span>
                )}
              </span>
              <Mono className={cn('shrink-0 text-body-s', isZero(m.net) ? 'text-fg-muted' : 'text-fg')}>
                {isZero(m.net) ? '—' : moneyShort(m.net)}
              </Mono>
            </button>
          </li>
        ))}
      </ul>
      <table className="hidden w-full table-fixed border-collapse text-left text-body-s md:table">
        <thead>
          <tr className="border-b border-line">
            <th scope="col" className={th}>
              Miesiąc
            </th>
            <th scope="col" className={cn(th, 'w-[120px] text-right')}>
              Netto
            </th>
            <th scope="col" className={cn(th, 'w-[120px] text-right')}>
              Opłacone
            </th>
            <th scope="col" className={cn(th, 'w-[80px] text-right')}>
              {settlement ? 'Rozliczenia' : 'Faktury'}
            </th>
            <th scope="col" className={cn(th, 'w-[88px] text-right')}>
              Godziny
            </th>
            <th scope="col" className={cn(th, 'hidden w-[120px] text-right lg:table-cell')}>
              Rok wcześniej
            </th>
          </tr>
        </thead>
        <tbody>
          {s.months.map((m) => {
            const zero = isZero(m.net) && m.invoices === 0;
            return (
              <tr
                key={m.month}
                onClick={() => onSelect(m.month)}
                className={cn(
                  'cursor-pointer border-b border-line transition-colors hover:bg-surface',
                  row(m) && 'bg-surface',
                )}
              >
                <td className="h-11 px-1">
                  <button
                    type="button"
                    aria-pressed={row(m)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(m.month);
                    }}
                    className={cn(
                      'rounded-xs text-left focus-visible:outline-2 focus-visible:outline-accent',
                      row(m) ? 'font-semibold text-fg' : 'text-fg',
                    )}
                  >
                    {capitalize(monthName(s.year, m.month))}
                  </button>
                </td>
                <MoneyCell amount={m.net} muted={zero} />
                <MoneyCell amount={m.paid} muted={zero} />
                <td className="px-1 text-right">
                  <Mono className={zero ? 'text-fg-muted' : 'text-fg'}>{zero ? '—' : m.invoices}</Mono>
                </td>
                <td className="px-1 text-right">
                  <Mono className={zero ? 'text-fg-muted' : 'text-fg'}>{zero ? '—' : clockText(m.seconds)}</Mono>
                </td>
                <td className="hidden px-1 text-right lg:table-cell">
                  <Mono className="text-fg-muted">{isZero(m.prevNet) ? '—' : moneyShort(m.prevNet)}</Mono>
                </td>
              </tr>
            );
          })}
          <tr>
            <td className="h-11 px-1 font-semibold text-fg">Razem</td>
            <MoneyCell amount={s.totals.net} strong />
            <MoneyCell amount={s.totals.paid} strong />
            <td className="px-1 text-right">
              <Mono className="font-semibold text-fg">{s.totals.invoices}</Mono>
            </td>
            <td className="px-1 text-right">
              <Mono className="font-semibold text-fg">{clockText(s.totals.seconds)}</Mono>
            </td>
            <td className="hidden px-1 text-right lg:table-cell">
              <Mono className="text-fg-muted">{isZero(s.totals.prevNet) ? '—' : moneyShort(s.totals.prevNet)}</Mono>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
}

function MoneyCell({ amount, muted = false, strong = false }: { amount: string; muted?: boolean; strong?: boolean }) {
  return (
    <td className="px-1 text-right">
      <Mono className={cn(muted ? 'text-fg-muted' : 'text-fg', strong && 'font-semibold')}>
        {muted ? '—' : moneyShort(amount)}
      </Mono>
    </td>
  );
}

/** „Z czego składa się miesiąc”: klienci (kwota, udział), po rozwinięciu projekty, „Bez projektu”, pozycje stałe. */
function MonthBreakdown({ stats: s }: { stats: BillingStats }) {
  const b = s.breakdown;
  const settlement = useContext(SettlementContext);
  const title = `${capitalize(monthName(s.year, b.month))} ${s.year}`;
  return (
    <Section aria-labelledby="billing-stats-breakdown">
      <SectionHeader
        title={<span id="billing-stats-breakdown">Z czego składa się {title.toLowerCase()}</span>}
        tone="strong"
        action={<Mono className="text-body-s font-semibold text-fg">{moneyAuto(b.net)}</Mono>}
      />
      {b.clients.length === 0 ? (
        <EmptyState
          compact
          title={`Brak ${settlement ? 'wysłanych rozliczeń' : 'wystawionych faktur'} w tym miesiącu (${title.toLowerCase()}).`}
        />
      ) : (
        <ul>
          {b.clients.map((c) => (
            <BreakdownClient key={c.client.id} client={c} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function BreakdownClient({ client: c }: { client: BillingStatsBreakdownClient }) {
  const [open, setOpen] = useState(false);
  const invoicesText = useInvoicesText();
  const panelId = `billing-stats-client-${c.client.id}`;
  return (
    <li className="border-b border-line">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="-mx-2 flex min-h-[52px] w-[calc(100%+16px)] items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent"
      >
        <ChevronDown
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className={cn('shrink-0 text-fg-muted transition-transform', !open && '-rotate-90')}
        />
        <ClientDot color={c.client.color} />
        <span className="flex min-w-0 flex-1 flex-col md:flex-row md:items-baseline md:gap-2.5">
          <span className="truncate text-body-s font-semibold text-fg">{c.client.name}</span>
          <span className="truncate text-caption text-fg-muted">
            {percentText(c.share)} · {invoicesText(c.invoices)}
          </span>
        </span>
        <Mono className="shrink-0 text-body-s text-fg">{moneyAuto(c.net)}</Mono>
      </button>
      {open && (
        <ul id={panelId} aria-label={`Projekty: ${c.client.name}`} className="pb-2 pl-7 md:pl-[46px]">
          {c.lines.map((l) => (
            <BreakdownLine key={lineKey(l)} line={l} />
          ))}
        </ul>
      )}
    </li>
  );
}

const lineKey = (l: BillingStatsLine) => (l.kind === 'project' ? (l.project?.id ?? 'p') : l.kind);

function BreakdownLine({ line: l }: { line: BillingStatsLine }) {
  const name = l.kind === 'project' ? (l.project?.name ?? '—') : l.kind === 'fixed' ? 'Pozycje stałe' : 'Bez projektu';
  return (
    <li className="flex min-h-[36px] items-center gap-3 py-1">
      <span className={cn('min-w-0 flex-1 truncate text-body-s', l.kind === 'project' ? 'text-fg' : 'text-fg-muted')}>
        {name}
      </span>
      {l.seconds > 0 && <Mono className="shrink-0 text-caption text-fg-muted">{clockText(l.seconds)}</Mono>}
      <Mono className="w-[96px] shrink-0 text-right text-body-s text-fg">{moneyAuto(l.net)}</Mono>
    </li>
  );
}

function TopClients({ stats: s }: { stats: BillingStats }) {
  return (
    <Section aria-labelledby="billing-stats-top">
      <SectionHeader title={<span id="billing-stats-top">Najwięcej w {s.year}</span>} tone="strong" />
      <ol>
        {s.topClients.map((c) => (
          <li key={c.client.id} className="flex min-h-[48px] flex-col justify-center gap-1.5 border-b border-line py-2">
            <div className="flex items-center gap-2.5">
              <ClientDot color={c.client.color} />
              <span className="min-w-0 flex-1 truncate text-body-s text-fg">{c.client.name}</span>
              <Mono className="shrink-0 text-body-s text-fg">{moneyShort(c.net)}</Mono>
            </div>
            <div className="flex items-center gap-2.5 pl-[18px]">
              <span className="h-1 flex-1 overflow-hidden rounded-full bg-surface" aria-hidden>
                <span className="block h-full rounded-full bg-fg-muted" style={{ width: `${c.share}%` }} />
              </span>
              <span className="w-[52px] shrink-0 text-right text-caption text-fg-muted">{percentText(c.share)}</span>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}
